import { STAFF_DUTY_LABELS, type StationStaff, staffDutySchema } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, Users } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { StaffPinDialog } from "../../../features/pos/pin-dialog.js";
import { usePosPermissions } from "../../../features/pos/queries.js";
import { useShiftPermissions, useStationStaff } from "../../../features/shifts/queries.js";
import {
  NoStations,
  ShiftStationField,
  ShiftsPage,
  shiftsSearchShape,
  useShiftStation,
} from "../../../features/shifts/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { StaffDialog } from "../../../features/shifts/staff-dialog.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";

const searchSchema = z.object({
  ...shiftsSearchShape,
  duty: staffDutySchema.optional().catch(undefined),
  active: z.enum(["true", "false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/vardiya/personel")({
  validateSearch: (search) => searchSchema.parse(search),
  component: StaffPage,
});

/**
 * İstasyon personeli: kullanıcı hesabı ile PERSONEL carisi arasındaki bağ. Fiziksel silme yoktur —
 * ayrılan personel PASİFE alınır (geçmiş vardiyalarda adı kalır).
 */
function StaffPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  return (
    <ShiftsPage
      title={t("menu.shiftStaff")}
      description={t("shifts.staff.description")}
      breadcrumb={[{ label: t("menu.shiftStaff") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <StaffBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        )
      }
    </ShiftsPage>
  );
}

function StaffBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useShiftPermissions();
  const search = Route.useSearch();
  const station = useShiftStation(companyId);
  const pos = usePosPermissions();
  const [editing, setEditing] = useState<{ staff: StationStaff | null } | null>(null);
  const [pinFor, setPinFor] = useState<StationStaff | null>(null);

  const setFilters = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters = {
    stationId: station.stationId ?? undefined,
    duty: search.duty,
    isActive: search.active === undefined ? undefined : search.active === "true",
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useStationStaff(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<StationStaff>[]>(
    () => [
      {
        id: "staffCode",
        header: t("shifts.staff.fields.staffCode"),
        hideable: false,
        exportValue: (row) => row.staffCode,
        cell: (row) => <span className="font-mono">{row.staffCode}</span>,
      },
      {
        id: "fullName",
        header: t("shifts.staff.fields.fullName"),
        exportValue: (row) => row.fullName,
        cell: (row) => row.fullName,
      },
      {
        id: "duty",
        header: t("shifts.staff.fields.duty"),
        exportValue: (row) => STAFF_DUTY_LABELS[row.duty],
        cell: (row) => STAFF_DUTY_LABELS[row.duty],
      },
      {
        id: "counterparty",
        header: t("shifts.staff.fields.counterparty"),
        exportValue: (row) => row.counterpartyName,
        cell: (row) =>
          row.counterpartyName ?? (
            <StatusBadge tone="warning">{t("shifts.staff.noCounterpartyShort")}</StatusBadge>
          ),
      },
      {
        id: "isActive",
        header: t("common.status"),
        exportValue: (row) => (row.isActive ? t("common.active") : t("common.inactive")),
        cell: (row) => (
          <StatusBadge tone={row.isActive ? "success" : "neutral"}>
            {row.isActive ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        ),
      },
      {
        id: "actions",
        header: t("common.actions"),
        exportValue: () => null,
        cell: (row) => {
          const actions = [
            permissions.canManage ? (
              <Button key="edit" variant="outline" size="sm" onClick={() => setEditing({ staff: row })}>
                {t("common.edit")}
              </Button>
            ) : null,
            // Kasa PIN'i YEREL KASA modülünündür: bayrak kapalıysa ya da okuma izni yoksa eylem çizilmez.
            pos.enabled && pos.canRead ? (
              <Button key="pin" variant="outline" size="sm" onClick={() => setPinFor(row)}>
                {t("pos.pin.action")}
              </Button>
            ) : null,
          ].filter((action) => action !== null);
          if (actions.length === 0) return <span className="text-muted-foreground">—</span>;
          return <span className="flex flex-wrap gap-2">{actions}</span>;
        },
      },
    ],
    [t, permissions.canManage, pos.enabled, pos.canRead],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <>
      {permissions.canManage ? (
        <div className="flex justify-end">
          <Button onClick={() => setEditing({ staff: null })}>
            <Plus aria-hidden="true" />
            {t("shifts.staff.newTitle")}
          </Button>
        </div>
      ) : null}
      <DataTable
        caption={t("shifts.staff.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Users />}
            title={t("shifts.staff.emptyTitle")}
            description={t("shifts.staff.emptyDescription")}
          />
        }
        filters={
          <>
            <ShiftStationField selection={station} />
            <FormField label={t("shifts.staff.fields.duty")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.duty ?? ""}
                  onChange={(event) =>
                    setFilters({
                      duty: event.target.value === "" ? undefined : staffDutySchema.parse(event.target.value),
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {staffDutySchema.options.map((duty) => (
                    <option key={duty} value={duty}>
                      {STAFF_DUTY_LABELS[duty]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("common.status")} className="w-40">
              {(control) => (
                <Select
                  {...control}
                  value={search.active ?? ""}
                  onChange={(event) =>
                    setFilters({
                      active:
                        event.target.value === "" ? undefined : (event.target.value as "true" | "false"),
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  <option value="true">{t("common.active")}</option>
                  <option value="false">{t("common.inactive")}</option>
                </Select>
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
      <StaffDialog
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        companyId={companyId}
        stations={station.stations}
        defaultStationId={station.stationId}
        staff={editing?.staff ?? null}
      />
      <StaffPinDialog
        open={pinFor !== null}
        onOpenChange={(next) => {
          if (!next) setPinFor(null);
        }}
        staff={pinFor}
      />
    </>
  );
}
